/**
 * DashboardOverview (NAU-247 / NAU-263 / NAU-267) — the redesigned read-only
 * "what needs my attention?" landing page. Composes the summary cards, priority
 * list, today's status, streak, recently-completed and insights panels.
 *
 * The summary cards, priority list and today's status read the backend
 * `/dashboard/status` foundation — one query, shared, so the numbers reconcile.
 * Recently-completed comes from `build*`, and anything still pending its backend
 * is clearly marked as demo data.
 */

import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { useOrganisation } from "@/contexts/OrganisationContext";
import {
  useGetDashboardStatusQueryControllerGetStatus,
  useGetDashboardStreakQueryControllerGetStreak,
} from "@/generated/dashboard/dashboard";
import {
  completionRateDelta,
  EMPTY_DASHBOARD_METRICS,
  kpisFromMetrics,
} from "@/lib/dashboard/card-status";
import { categoryDestination } from "@/lib/dashboard/category-map";
import {
  buildRecentlyCompleted,
  type OverviewInputs,
} from "@/lib/dashboard/dashboard-overview";
import { buildInsights } from "@/lib/dashboard/insights";
import type { DashboardPeriod } from "@/lib/dashboard/period";
import type { PriorityLink } from "@/lib/dashboard/priority-list";
import { mapToTodaysStatus } from "@/lib/dashboard/todays-status";
import { getPeriodRange, getPreviousPeriodRange } from "@/lib/date-range";
import type { FeatureKey } from "@/lib/features";

import { InsightsPanel } from "./InsightsPanel";
import { OnTimeStreakCard } from "./OnTimeStreakCard";
import { PriorityListSection } from "./PriorityListSection";
import { RecentlyCompleted } from "./RecentlyCompleted";
import { StatTiles } from "./StatTiles";
import { TodaysStatusPanel } from "./TodaysStatusPanel";

function getGreeting(firstName?: string | null): string {
  const hour = new Date().getHours();
  const timeOfDay =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const name = firstName?.trim();
  return name ? `${timeOfDay}, ${name}! 👋` : `${timeOfDay}! 👋`;
}

interface DashboardOverviewProps {
  inputs: OverviewInputs;
  nurseryId: string;
  /** The selected dashboard period; scopes the summary cards and the delta. */
  period: DashboardPeriod;
  firstName?: string | null;
  periodSelector?: React.ReactNode;
}

export function DashboardOverview({
  inputs,
  nurseryId,
  period,
  firstName,
  periodSelector,
}: DashboardOverviewProps) {
  const navigate = useNavigate();

  // Plan-gated categories (Monitoring, Compliance) render as an "Upgrade" row
  // in Today's status when the org's plan doesn't include them. Read the org's
  // available features; while it's still resolving, pass `undefined` so nothing
  // is locked prematurely (avoids a flash of the upgrade state).
  const { activeOrg, isLoading: orgLoading } = useOrganisation();
  const availableFeatures = useMemo<FeatureKey[] | undefined>(() => {
    if (orgLoading || !activeOrg) return undefined;
    // The DTO types features as raw strings; narrow at this boundary so the
    // rollup can compare against typed FeatureKey values.
    return (activeOrg.features ?? []) as FeatureKey[];
  }, [activeOrg, orgLoading]);

  // Resolve the selected period — and the equal-length one before it for the
  // delta — to date windows via the shared NAU-262 helpers, so the cards scope
  // exactly like the rest of the dashboard.
  const range = useMemo(() => getPeriodRange(period), [period]);
  const prev = useMemo(() => getPreviousPeriodRange(period), [period]);

  // Single shared read of the status foundation for the selected period: feeds
  // the summary cards, the priority list and today's status, so they reconcile.
  const {
    data: statusData,
    isPending: statusPending,
    isError: statusError,
  } = useGetDashboardStatusQueryControllerGetStatus(
    nurseryId,
    {
      periodStart: range.startDate.toISOString(),
      periodEnd: range.endDate.toISOString(),
    },
    { query: { enabled: Boolean(nurseryId), refetchOnMount: "always" } },
  );

  // Previous period, for the real vs-last-period completion delta.
  const { data: prevStatusData } =
    useGetDashboardStatusQueryControllerGetStatus(
      nurseryId,
      {
        periodStart: prev.startDate.toISOString(),
        periodEnd: prev.endDate.toISOString(),
      },
      { query: { enabled: Boolean(nurseryId), refetchOnMount: "always" } },
    );

  // On-time streak (NAU-268): whole-nursery and "as of now", so it is
  // independent of the selected period.
  const { data: streakData } = useGetDashboardStreakQueryControllerGetStreak(
    nurseryId,
    undefined,
    {
      query: { enabled: Boolean(nurseryId), refetchOnMount: "always" },
    },
  );

  const metrics = statusData?.data?.metrics ?? EMPTY_DASHBOARD_METRICS;
  const statusItems = useMemo(
    () => statusData?.data?.items ?? [],
    [statusData?.data?.items],
  );

  const prevMetrics = prevStatusData?.data?.metrics;

  const kpis = useMemo(
    () =>
      kpisFromMetrics(
        metrics,
        completionRateDelta(
          metrics.completionRate,
          prevMetrics?.completionRate,
          prevMetrics?.totalDueCount,
        ),
      ),
    [metrics, prevMetrics?.completionRate, prevMetrics?.totalDueCount],
  );

  const insights = useMemo(
    () => buildInsights(metrics, prevMetrics, statusItems),
    [metrics, prevMetrics, statusItems],
  );

  const todaysStatus = useMemo(
    () => mapToTodaysStatus(statusItems, { availableFeatures }),
    [statusItems, availableFeatures],
  );
  const recentlyCompleted = useMemo(
    () => buildRecentlyCompleted(inputs),
    [inputs],
  );

  const todayCompleted = inputs.dayItems.filter(
    (i) => i.status === "COMPLETED" || i.status === "COMPLETED_LATE",
  ).length;
  const todayTotal = inputs.dayItems.length;

  // Navigation
  const onView = (link: PriorityLink) => {
    switch (link.kind) {
      case "checklist":
        navigate({ to: "/checklists/$id", params: { id: link.id } });
        return;
      case "action":
        navigate({
          to: "/follow-up-actions/$actionId",
          params: { actionId: link.id },
        });
        return;
      case "incident":
        navigate({
          to: "/incidents/$incidentId",
          params: { incidentId: link.id },
        });
        return;
      case "compliance":
        navigate({
          to: "/compliance/instance/$instanceId",
          params: { instanceId: link.id },
        });
        return;
      case "monitoring":
        // `monitoring` covers both hot-holding and check-failure alerts, so
        // link.id could be either and only the former has a detail page — open
        // the alerts list until the kinds are split apart.
        navigate({ to: "/monitoring", search: { tab: "alerts" } });
        return;
      case "event":
        navigate({
          to: "/event-records/$eventRecordId",
          params: { eventRecordId: link.id },
        });
        return;
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto bg-white pb-10">
      {/* Header */}
      <div className="flex w-full flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col">
          <span className="text-heading-1 font-heading-1 text-primary-navy-800">
            {getGreeting(firstName)}
          </span>
          <span className="text-body font-body text-subtext-color">
            Here’s an overview of your nursery…
          </span>
        </div>
        {periodSelector}
      </div>

      {/* Body: left column (tiles → priority → recent/insights) +
          right column (today's status + streak) starting at the top */}
      <div className="grid w-full gap-6 xl:grid-cols-[1fr_340px] mobile:grid-cols-1">
        <div className="flex min-w-0 flex-col gap-6">
          <StatTiles
            kpis={kpis}
            period={period}
            loading={statusPending}
            // TODO(NAU-263 spawn): History is the closest tab, not an exact
            // match — the tile counts items the lateness engine bands ACTION for
            // the period, while History lists EXPIRED ones all-time. An overdue
            // item the cron hasn't expired yet shows in neither tab. Needs a
            // combined overdue/missed view, or a status search param.
            onViewMissed={() =>
              navigate({ to: "/checklist-manager", search: { tab: "history" } })
            }
            onViewIssues={() =>
              navigate({ to: "/checklist-manager", search: { tab: "failed" } })
            }
            onViewActions={() =>
              navigate({
                to: "/task-manager",
                search: { tab: "follow-up-actions" },
              })
            }
          />
          <PriorityListSection
            items={statusItems}
            loading={statusPending}
            onOpen={onView}
            // The list spans all four categories; To do is the closest
            // aggregate (monitoring aside, which lives on its own tab).
            onViewAll={() => navigate({ to: "/checklists" })}
          />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr_1.6fr] mobile:grid-cols-1">
            <RecentlyCompleted
              items={recentlyCompleted}
              onViewAll={() =>
                navigate({
                  to: "/checklist-manager",
                  search: { tab: "history" },
                })
              }
            />
            <InsightsPanel
              insights={insights}
              period={period}
              loading={statusPending}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <TodaysStatusPanel
            domains={todaysStatus}
            completed={todayCompleted}
            total={todayTotal}
            loading={statusPending}
            error={statusError}
            onGoToToday={() => navigate({ to: "/checklists" })}
            onSelectCategory={(key) => {
              // Coming-soon rows have no destination; the panel already renders
              // them inert, so this is belt-and-braces.
              const dest = categoryDestination(key);
              if (!dest) return;
              // Plan-gated rows go to the same page, where the route-level
              // FeatureGate shows the upgrade prompt instead of the content.
              navigate({ to: dest.to, search: { tab: dest.tab } });
            }}
          />
          <OnTimeStreakCard
            days={streakData?.data?.currentStreakDays ?? null}
            graceHours={streakData?.data?.graceHours}
          />
        </div>
      </div>
    </div>
  );
}
